import React, {createContext, useContext, useState, ReactNode, useRef, useEffect, ElementType} from 'react'
import {useIntl} from 'react-intl'
import {
    Box,
    HStack,
    Text,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerHeader,
    DrawerBody,
    DrawerFooter,
    useMultiStyleConfig,
    FlexProps,
    TextProps,
    StackProps,
    SlideOptions,
} from '@chakra-ui/react'
import {IconButton, Icon} from '_uiComponents'
import {IconNameTypes} from '../../types'
import {useViewport} from '_customHooks'

type LogicalPlacement = 'start' | 'end'

type DrawerPlacement = SlideOptions['direction'] | LogicalPlacement

interface DrawerOptions {
    title?: string | ReactNode
    titleIcon?: IconNameTypes
    body: ReactNode
    footer?: ReactNode
    size?: 'md' | 'lg'
    variant?: string
    motionProps?: Record<string, unknown>
    autoClosingTimeout?: number
    closingIcon?: IconNameTypes
    closingIconSize?: 'sm' | 'md' | 'lg'
    placement?: DrawerPlacement
    onOpen?: () => void
    onClose?: (...params) => void
    onBack?: () => void
    titleAs?: ElementType
}

interface DrawerContextType {
    openDrawer: (options: DrawerOptions) => void
    closeDrawer: () => void
    updateDrawerTitle: (title: string | ReactNode) => void
    updateDrawerFooter: (footer: ReactNode) => void
    updateDrawerBody: (body: ReactNode) => void
    updateClosingIcon: (icon: IconNameTypes, size: 'sm' | 'md' | 'lg') => void
    isOpen: boolean
    titleAs?: ElementType
}

const DrawerContext = createContext<DrawerContextType | undefined>(undefined)

export const DrawerProvider = React.memo(({children}: {children: ReactNode}) => {
    const styles = useMultiStyleConfig('Drawer') as Record<string, FlexProps & TextProps & StackProps>
    const {formatMessage} = useIntl()
    const {isMobile} = useViewport()

    const drawerRef = useRef<HTMLDivElement>(null)
    const drawerContentRef = useRef<HTMLDivElement>(null)
    const drawerTitleId = 'drawer-title'

    const [isOpen, setIsOpen] = useState(false)
    const [variant, setVariant] = useState<string | undefined>()
    const [drawerTitle, setDrawerTitle] = useState<string | ReactNode | undefined>()
    const [drawerTitleIcon, setDrawerTitleIcon] = useState<IconNameTypes | undefined>()
    const [drawerBody, setDrawerBody] = useState<ReactNode | null>(null)
    const [drawerFooter, setDrawerFooter] = useState<ReactNode | null>(null)
    const [drawerSize, setDrawerSize] = useState<string>('lg')
    const [closingIcon, setClosingIcon] = useState<IconNameTypes>('IconClose')
    const [closingIconSize, setClosingIconSize] = useState<'sm' | 'md' | 'lg'>('md')
    const [drawerMotionProps, setDrawerMotionProps] = useState<DrawerOptions['motionProps']>(undefined)
    const [closingTimeoutId, setClosingTimeoutId] = useState<number | undefined>()
    const [drawerPlacement, setDrawerPlacement] = useState<DrawerPlacement>()
    const onOpenCallbackRef = useRef<(() => void) | undefined>()
    const onCloseCallbackRef = useRef<(() => void) | undefined>()
    const [onBackCallback, setOnBackCallback] = useState<(() => void) | undefined>()
    const [shouldStopTimer, setShouldStopTimer] = useState<boolean>(false)
    const [titleAs, setTitleAs] = useState<DrawerOptions['titleAs']>('p')

    const openDrawer = ({
        title,
        titleIcon,
        body,
        footer,
        size = 'lg',
        variant,
        motionProps,
        onOpen,
        onClose,
        onBack,
        autoClosingTimeout,
        placement,
        closingIcon = 'IconClose',
        closingIconSize = 'md' /*, onBack */,
        titleAs = 'p',
    }: DrawerOptions) => {
        setDrawerTitle(title)
        setDrawerTitleIcon(titleIcon)
        setDrawerBody(body)
        setDrawerFooter(footer)
        setDrawerSize(size)
        onOpenCallbackRef.current = onOpen
        onCloseCallbackRef.current = onClose
        setOnBackCallback(() => onBack)
        setVariant(variant)
        setClosingTimeoutId(autoClosingTimeout)
        setShouldStopTimer(false)
        setDrawerPlacement(placement ? placement : isMobile ? 'bottom' : 'right')
        setIsOpen(true)
        setClosingIcon(closingIcon)
        setClosingIconSize(closingIconSize)
        setDrawerMotionProps(motionProps)
        setTitleAs(titleAs)
    }

    const closeDrawer = () => {
        if (onCloseCallbackRef.current) onCloseCallbackRef.current()
        setIsOpen(false)
    }

    const updateDrawerTitle = (title: string | ReactNode) => {
        setDrawerTitle(title)
    }

    const updateDrawerBody = (body: ReactNode) => {
        setDrawerBody(body)
    }

    const updateDrawerFooter = (footer: ReactNode) => {
        setDrawerFooter(footer)
    }

    const updateClosingIcon = (icon: IconNameTypes, size: 'sm' | 'md' | 'lg') => {
        setClosingIcon(icon)
        setClosingIconSize(size)
    }

    useEffect(() => {
        if (!closingTimeoutId) return

        const timeoutId = window.setTimeout(() => {
            closeDrawer()
        }, closingTimeoutId)

        if (shouldStopTimer) {
            clearTimeout(timeoutId)
        }

        return () => {
            clearTimeout(timeoutId)
        }
    }, [closingTimeoutId, shouldStopTimer])

    /**
     * Since Chakra does not support setup of `aria-labelledby` parameter on drawer content
     * this setup on opening ensures that it is configured correctly pointing to the text that
     * actually contains the title label of the Drawer.
     */
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => {
                if (drawerContentRef.current) {
                    drawerContentRef.current.setAttribute('aria-labelledby', drawerTitleId)
                }
            }, 500)

            if (onOpenCallbackRef.current) onOpenCallbackRef.current()
        }
        // `openDrawer` is recreated on every render; only `isOpen` toggling should retrigger this effect.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen])

    return (
        <DrawerContext.Provider value={{openDrawer, closeDrawer, updateDrawerTitle, updateDrawerBody, updateDrawerFooter, updateClosingIcon, isOpen, titleAs}}>
            {children}
            <Drawer
                isOpen={isOpen}
                onClose={closeDrawer}
                size={drawerSize}
                variant={variant}
                placement={drawerPlacement}
                returnFocusOnClose={variant === 'shopTheImage' ? false : undefined}
                autoFocus={variant === 'shopTheImage' ? false : undefined}
            >
                <DrawerOverlay />
                <DrawerContent ref={drawerContentRef} onMouseEnter={() => setShouldStopTimer(true)} motionProps={drawerMotionProps}>
                    <DrawerHeader>
                        {drawerTitle && (
                            <HStack {...styles.titleRow}>
                                {drawerTitleIcon && (
                                    <Box mr={drawerSize === 'md' ? 'spacing.4' : '0'}>
                                        <Icon aria-hidden={true} name={drawerTitleIcon} size={drawerSize === 'md' ? 'sm' : 'lg'} />
                                    </Box>
                                )}
                                <Text as={titleAs} id={drawerTitleId} {...styles.headerTitle}>
                                    {drawerTitle}
                                </Text>
                            </HStack>
                        )}
                        <Box {...styles.closeIcon}>
                            <IconButton
                                size="iconButtonSm"
                                icon={<Icon name={closingIcon} size={closingIconSize} />}
                                onClick={closingIcon === 'IconChevronLeft' ? onBackCallback : closeDrawer}
                                aria-label={formatMessage({id: 'alt.link.close', defaultMessage: 'Close'})}
                            />
                        </Box>
                    </DrawerHeader>

                    <DrawerBody ref={drawerRef}>{drawerBody}</DrawerBody>

                    {drawerFooter && <DrawerFooter>{drawerFooter}</DrawerFooter>}
                </DrawerContent>
            </Drawer>
        </DrawerContext.Provider>
    )
})

DrawerProvider.displayName = 'DrawerProvider'

export const useDrawer = (): DrawerContextType => {
    const context = useContext(DrawerContext)
    if (!context) {
        throw new Error('useDrawer must be used within a DrawerProvider')
    }
    return context
}
